import { parseSampleRate } from '../utils/parseSampleRate.js';
import { baggageHeaderToDynamicSamplingContext } from './baggage.js';
import { generateTraceId, generateSpanId } from './propagationContext.js'; // eslint-disable-next-line @sentry-internal/sdk/no-regexp-constructor -- RegExp is used for readability here

var TRACEPARENT_REGEXP = new RegExp('^[ \\t]*' + // whitespace
'([0-9a-f]{32})?' + // trace_id
'-?([0-9a-f]{16})?' + // span_id
'-?([01])?' + // sampled
'[ \\t]*$');
/**
 * Extract transaction context data from a `sentry-trace` header.
 *
 * @param traceparent Traceparent string
 *
 * @returns Object containing data from the header, or undefined if traceparent string is malformed
 */

function extractTraceparentData(traceparent) {
  if (!traceparent) {
    return undefined;
  }

  var matches = traceparent.match(TRACEPARENT_REGEXP);

  if (!matches) {
    return undefined;
  }

  var parentSampled;

  if (matches[3] === '1') {
    parentSampled = true;
  } else if (matches[3] === '0') {
    parentSampled = false;
  }

  return {
    traceId: matches[1],
    parentSampled,
    parentSpanId: matches[2]
  };
}
/**
 * Create a propagation context from incoming headers or
 * creates a minimal new one if the headers are undefined.
 */


function propagationContextFromHeaders(sentryTrace, baggage) {
  var traceparentData = extractTraceparentData(sentryTrace);
  var dynamicSamplingContext = baggageHeaderToDynamicSamplingContext(baggage);

  if (!(traceparentData !== null && traceparentData !== void 0 && traceparentData.traceId)) {
    return {
      traceId: generateTraceId(),
      sampleRand: Math.random()
    };
  }

  var sampleRand = getSampleRandFromTraceparentAndDsc(traceparentData, dynamicSamplingContext); // The sample_rand on the DSC needs to be generated based on traceparent + baggage.

  if (dynamicSamplingContext) {
    dynamicSamplingContext.sample_rand = sampleRand.toString();
  }

  var {
    traceId,
    parentSpanId,
    parentSampled
  } = traceparentData;
  return {
    traceId,
    parentSpanId,
    sampled: parentSampled,
    dsc: dynamicSamplingContext || {},
    // If we have traceparent data but no DSC it means we are not head of trace and we must freeze it
    sampleRand
  };
}
/**
 * Create sentry-trace header from span context values.
 */


function generateSentryTraceHeader() {
  var traceId = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : generateTraceId();
  var spanId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : generateSpanId();
  var sampled = arguments.length > 2 ? arguments[2] : undefined;
  var sampledString = '';

  if (sampled !== undefined) {
    sampledString = sampled ? '-1' : '-0';
  }

  return "".concat(traceId, "-").concat(spanId).concat(sampledString);
}
/**
 * Given any combination of an incoming trace, generate a sample rand based on its defined semantics.
 *
 * Read more: https://develop.sentry.dev/sdk/telemetry/traces/#propagated-random-value
 */


function getSampleRandFromTraceparentAndDsc(traceparentData, dsc) {
  // When there is an incoming sample rand use it.
  var parsedSampleRand = parseSampleRate(dsc === null || dsc === void 0 ? void 0 : dsc.sample_rand);

  if (parsedSampleRand !== undefined) {
    return parsedSampleRand;
  } // Otherwise, if there is an incoming sampling decision + sample rate, generate a sample rand that would lead to the same sampling decision.


  var parsedSampleRate = parseSampleRate(dsc === null || dsc === void 0 ? void 0 : dsc.sample_rate);

  if (parsedSampleRate && (traceparentData === null || traceparentData === void 0 ? void 0 : traceparentData.parentSampled) !== undefined) {
    return traceparentData.parentSampled ? // Returns a sample rand with positive sampling decision [0, sampleRate)
    Math.random() * parsedSampleRate : // Returns a sample rand with negative sampling decision [sampleRate, 1)
    parsedSampleRate + Math.random() * (1 - parsedSampleRate);
  } else {
    // If nothing applies, return a random sample rand.
    return Math.random();
  }
}

export { TRACEPARENT_REGEXP, extractTraceparentData, generateSentryTraceHeader, propagationContextFromHeaders };